import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { WorkflowTemplate, Agent } from "../api/client.js";
import {
  listWorkflowTemplates,
  installWorkflowTemplate,
  listAgents,
} from "../api/client.js";

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "security", label: "Security" },
  { value: "data", label: "Data" },
  { value: "research", label: "Research" },
  { value: "automation", label: "Automation" },
];

const DIFFICULTIES = [
  { value: "all", label: "All Levels" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: "bg-green-100 text-green-700",
  intermediate: "bg-amber-100 text-amber-700",
  advanced: "bg-red-100 text-red-700",
};

export function WorkflowTemplateGallery() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [search, setSearch] = useState("");
  const [installModal, setInstallModal] = useState<WorkflowTemplate | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (category !== "all") params.category = category;
      if (difficulty !== "all") params.difficulty = difficulty;
      if (search) params.search = search;
      const [tmplRes, agentRes] = await Promise.all([
        listWorkflowTemplates(params),
        listAgents(),
      ]);
      setTemplates(tmplRes.templates);
      setAgents(agentRes.agents);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [category, difficulty, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-950">Workflow Templates</h1>
        <p className="mt-1 text-sm text-ink-500">
          Pre-built workflows you can install and customize
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
        />
        <div className="flex gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                category === cat.value
                  ? "bg-ink-950 text-white"
                  : "bg-surface-2 text-ink-600 hover:bg-surface-3"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.value}
              onClick={() => setDifficulty(d.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                difficulty === d.value
                  ? "bg-ink-950 text-white"
                  : "bg-surface-2 text-ink-600 hover:bg-surface-3"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="py-20 text-center text-ink-400">No templates found.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((tmpl) => (
            <div
              key={tmpl.id}
              className="group rounded-xl border border-ink-100 bg-white p-5 transition-shadow hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-ink-900">{tmpl.name}</h3>
                  <span className="text-xs text-ink-400">{tmpl.category}</span>
                </div>
                <div className="flex items-center gap-2">
                  {tmpl.featured && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                      Featured
                    </span>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${DIFFICULTY_COLORS[tmpl.difficulty] ?? ""}`}>
                    {tmpl.difficulty}
                  </span>
                </div>
              </div>

              <p className="mb-3 line-clamp-2 text-sm text-ink-500">
                {tmpl.description ?? "No description"}
              </p>

              <div className="mb-3 flex items-center gap-2 text-xs text-ink-400">
                <span>{tmpl.definition.nodes.length} nodes</span>
                <span>{tmpl.definition.edges.length} edges</span>
                {tmpl.installCount > 0 && <span>{tmpl.installCount} installs</span>}
              </div>

              <div className="mb-3 flex flex-wrap gap-1">
                {tmpl.tags.slice(0, 4).map((tag) => (
                  <span key={tag} className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-ink-500">
                    {tag}
                  </span>
                ))}
              </div>

              <button
                onClick={() => setInstallModal(tmpl)}
                className="w-full rounded-lg bg-accent-600 py-2 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-accent-700"
              >
                Use Template
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Install modal */}
      {installModal && (
        <InstallWorkflowModal
          template={installModal}
          agents={agents}
          onClose={() => setInstallModal(null)}
          onInstalled={(workflowId) => {
            setInstallModal(null);
            fetchData();
            navigate(`/workflows/${workflowId}/edit`);
          }}
        />
      )}
    </div>
  );
}

function InstallWorkflowModal({
  template,
  agents,
  onClose,
  onInstalled,
}: {
  template: WorkflowTemplate;
  agents: Agent[];
  onClose: () => void;
  onInstalled: (workflowId: string) => void;
}) {
  const [name, setName] = useState(template.name);
  const [agentId, setAgentId] = useState(agents[0]?.id ?? "");
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInstall = async () => {
    if (!agentId) {
      setError("Please select an agent");
      return;
    }
    setInstalling(true);
    setError(null);
    try {
      const result = await installWorkflowTemplate(
        template.id,
        agentId,
        name !== template.name ? name : undefined,
      );
      onInstalled(result.workflow.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Installation failed");
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-bold text-ink-950">Use Template</h2>
        <p className="mb-4 text-sm text-ink-500">
          Create a new workflow from "{template.name}"
        </p>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Workflow Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Agent</label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              {agents.length === 0 && <option value="">No agents available</option>}
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-600">{error}</div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-ink-200 py-2 text-sm font-medium text-ink-600 hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            onClick={handleInstall}
            disabled={installing || !name.trim() || !agentId}
            className="flex-1 rounded-lg bg-accent-600 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {installing ? "Creating..." : "Create Workflow"}
          </button>
        </div>
      </div>
    </div>
  );
}
